import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLazyQuery, useMutation } from '@apollo/client';

import { logError } from 'commons/logger';
import getZipcode from 'commons/getZipcode';
import usePDPPrice from 'data-hooks/usePDPPrice';
import { UPDATE_CART_ITEM } from 'mocks/mutations';
import Quantity from 'components/consumer/Quantity';
import { GET_CART, GET_CHECKOUT_SUMMARY } from 'mocks/queries';
import useProductFavoriteV2 from 'data-hooks/useProductFavoriteV2';
import useUserCartEvent from 'data-hooks/useEvents/useUserCartEvent';
import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import { getCheckoutRef } from 'components/consumer/checkout/utils/checkout';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';

const QuantitySelect = ({
  issue,
  product,
  removeProduct,
  removeProductLoading,
  cartToken,
  viewType,
  onAddError,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [getCart, { loading: getCartLoading }] = useLazyQuery(GET_CART, {
    fetchPolicy: 'network-only',
  });
  const [
    getCheckoutSummary,
    { loading: getCheckoutSummaryLoading },
  ] = useLazyQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'network-only',
  });
  const [updateCartItem] = useMutation(UPDATE_CART_ITEM);

  const { data: basicDetails } = useProductBasicDetails(
    product?.product_id,
    null
  );

  const { data: priceData } = usePDPPrice(product?.product_id, null);
  const { isClearance } = priceData || {};
  const { isFavorited } = useProductFavoriteV2();

  const { removeFromCartEvent } = useUserCartEvent(
    basicDetails,
    {},
    priceData,
    isClearance,
    isFavorited
  );

  const updateQuantity = React.useCallback(
    async newQuantity => {
      try {
        if (loading) {
          return;
        }

        onAddError(null);
        setLoading(true);
        const res = await updateCartItem({
          variables: {
            cart_id: product.id,
            quantity: newQuantity,
            product_id: product.product_id.toString(),
            pickLater: product.pickLater,
            productOptions: product.options.map(option => ({
              option_id: parseInt(option.optionTypeId, 10),
              option_value_id: parseInt(option.value, 10),
            })),
            zipCode: getZipcode(),
          },
        });

        const errors = res.data?.updateRequest?.errors ?? null;
        if (!errors) {
          const zipCode = getZipcode();
          const checkoutRef = getCheckoutRef();
          await Promise.all([
            getCart({
              context: { noBatch: true },
              variables: {
                zipCode,
                ...(checkoutRef ? { checkoutRef } : {}),
              },
            }),
            getCheckoutSummary({
              context: { noBatch: true },
              variables: {
                ...(checkoutRef ? { checkoutRef } : {}),
                input: {
                  zipcode: zipCode,
                },
              },
            }),
          ]);
        } else {
          onAddError(errors[0]?.message ?? 'Error occurred. Please try again.');
        }
      } catch (e) {
        onAddError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [
      getCart,
      getCheckoutSummary,
      loading,
      onAddError,
      product.id,
      product.options,
      product.pickLater,
      product.product_id,
      updateCartItem,
    ]
  );

  const minQuantity = React.useMemo(() => {
    const minQty = product.product.attributes.filter(
      ({ code }) => code === 'min_qty'
    )[0];
    return minQty?.value ? parseInt(minQty.value, 10) : 0;
  }, [product.product.attributes]);

  return (
    <div
      className={classNames(
        'relative inline-flex items-center justify-center',
        { 'mt-4 md:mt-0': viewType === 'cart-only' }
      )}
    >
      <Quantity
        issue={issue}
        minQuantity={minQuantity}
        quantity={product.quantity}
        onAddItem={() => {
          updateQuantity(product.quantity + 1).catch(e => {
            logError(e);
          });
        }}
        onRemoveItem={() => {
          if (issue || product.quantity === 1) {
            onAddError(null);
            removeProduct(product);
            removeFromCartEvent(product, cartToken);
          } else {
            updateQuantity(product.quantity - 1).catch(e => {
              logError(e);
            });
          }
        }}
        onRemoveItemMinQuantity={() => {
          // Remove product if min qty is 1
          removeProduct(product);
          if (product.quantity === 1) {
            removeFromCartEvent(product, cartToken);
          }
        }}
      />

      {(loading ||
        getCartLoading ||
        getCheckoutSummaryLoading ||
        removeProductLoading) && (
        <div
          className={classNames(
            'relative inline-flex items-center justify-center',
            { 'mt-4 md:mt-0': viewType === 'cart-only' }
          )}
        >
          <ProgressLoader size={10} />
        </div>
      )}
    </div>
  );
};

QuantitySelect.propTypes = {
  issue: PropTypes.object,
  product: PropTypes.shape({
    product: PropTypes.shape({
      attributes: PropTypes.array,
    }),
    product_id: PropTypes.string,
    id: PropTypes.number,
    quantity: PropTypes.number,
    options: PropTypes.array,
    pickLater: PropTypes.bool,
  }).isRequired,
  removeProduct: PropTypes.func.isRequired,
  removeProductLoading: PropTypes.bool,
  cartToken: PropTypes.string,
  viewType: PropTypes.string,
  onAddError: PropTypes.func,
};

export default QuantitySelect;
